import { BAND_LABEL, INDUSTRIES } from '@cyberscore/contracts';
import { ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { href, navigate } from '../../lib/router';
import { money } from '../clients/ClientRecord';
import { assistantError, useAskBook } from './api';

const EXAMPLES = ['Which manufacturers renewing this quarter have exceptions?', 'Our weakest healthcare clients', 'Bound clients renewing within 60 days, biggest premium first']; // prettier-ignore

/**
 * Ask about the book (task 74): the question is read as the list's own filters and answered by the list,
 * so the clients and figures shown are the data, not a model's words.
 */
export function BookAsk({ onOpen }: { onOpen: (customerKey: string) => void }) {
  const ask = useAskBook();
  const [q, setQ] = useState('');
  const go = (question: string) => {
    setQ(question);
    if (question.trim().length >= 3) ask.mutate(question.trim());
  };
  const r = ask.data;
  return (
    <section className="card pad book-ask" aria-labelledby="book-ask-title">
      <h2 id="book-ask-title" className="section-title"><Sparkles size={16} aria-hidden /> Ask about your book</h2>
      <form className="row gap book-ask-form" onSubmit={(e) => { e.preventDefault(); go(q); }}>
        <label htmlFor="book-q" className="sr-only">Your question</label>
        <input id="book-q" className="input grow" maxLength={500} placeholder={EXAMPLES[0]} value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="submit" className="btn btn-primary" disabled={ask.isPending || q.trim().length < 3}>{ask.isPending && <Loader2 size={14} className="spin" />}Ask</button>
      </form>
      {!r && !ask.isError && <div className="muted small book-examples">Try: {EXAMPLES.map((e, i) => <button key={e} type="button" className="linklike" onClick={() => go(e)}>{e}{i < EXAMPLES.length - 1 ? ';' : ''}</button>)}</div>}
      {ask.isError && <div className="result bad" role="alert">{assistantError(ask.error)}</div>}
      {r && (
        <div className="book-answer" role="status">
          <p className="small"><b>Read as:</b> {r.reading} {r.how === 'keywords' && <span className="muted">(by keywords: no model answer was usable)</span>}</p>
          <ul className="book-facts small">{r.facts.map((f) => <li key={f}>{f}</li>)}</ul>
          {r.clients.length > 0 && (
            <div className="table-wrap">
              <table className="grid small">
                <thead><tr><th>Client</th><th>Industry</th><th className="num">CyberScore</th><th className="num">Premium</th><th>Renews</th></tr></thead>
                <tbody>
                  {r.clients.map((c) => (
                    <tr key={c.customer_key}>
                      <td><button type="button" className="linklike cell-title" onClick={() => onOpen(c.customer_key)}>{c.customer_name}</button>{c.exceptions > 0 && <span className="pill bad small-pill">{c.exceptions} exception{c.exceptions === 1 ? '' : 's'}</span>}</td>
                      <td>{c.industry ? (INDUSTRIES[c.industry as keyof typeof INDUSTRIES] ?? c.industry) : '—'}</td>
                      <td className="num">{c.score === null ? '—' : <>{c.score} <span className="muted">{c.band ? BAND_LABEL[c.band] : ''}</span></>}</td>
                      <td className="num">{c.premium === null ? '—' : money(c.premium)}</td>
                      <td>{c.renewal_date ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="row gap mt-s">
            <button type="button" className="btn btn-sm" onClick={() => navigate(`${href.clients()}${r.list_query}`)}>Show these in the list <ArrowRight size={14} /></button>
            <span className="muted small">Drafted by the assistant from your clients list; the list decides who is in it.</span>
          </div>
        </div>
      )}
    </section>
  ); // prettier-ignore
}
